/**
 * The desktop tray popover is a separate ToDesktop window that loads this route: a "panel" window
 * on macOS and a "menubar" window on Windows. It shares the session with the main window but runs
 * its own copy of the app, so it only renders the review queue and asks the main window to open
 * anything the user clicks.
 */
export const DESKTOP_TRAY_PATH = '/desktop/tray';

/** Stable object ID assigned to cubic's tray in ToDesktop Builder. */
export const DESKTOP_TRAY_ID = 'ygmtYSOwyBe3J9ncfd-Nc';

/**
 * JS event the tray icon sends to every window when clicked on macOS, set as the icon's left-click
 * action in ToDesktop Builder. The popover opens itself on it.
 */
export const DESKTOP_TRAY_CLICK_EVENT = 'tray-click';

const TRAY_CHANNEL_NAME = 'cubic-desktop-tray';

type TrayWindowMessage = { type: 'open'; path: string };

export function isDesktopTrayPath(pathname: string): boolean {
  return pathname === DESKTOP_TRAY_PATH;
}

/** Asks the main window to open `path`; both windows share an origin, so BroadcastChannel reaches it. */
export function requestOpenInMainWindow(path: string): void {
  const channel = new BroadcastChannel(TRAY_CHANNEL_NAME);
  channel.postMessage({ type: 'open', path } satisfies TrayWindowMessage);
  channel.close();
}

/** Main-window side of {@link requestOpenInMainWindow}. Returns an unsubscribe for effect cleanup. */
export function subscribeToTrayWindowRequests(onOpen: (path: string) => void): () => void {
  const channel = new BroadcastChannel(TRAY_CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<unknown>) => {
    const message = event.data as Partial<TrayWindowMessage> | null;
    if (message?.type === 'open' && typeof message.path === 'string') {
      onOpen(message.path);
    }
  };
  return () => channel.close();
}
